import React, { useCallback, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { collection, getDocs } from "firebase/firestore";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { ATTENDANCE_BRANCHES } from "../../config/attendanceBranches";
import { getVisibleAttendanceBranches } from "../../config/appBranches";
import { resolveCollectionName } from "../../services/attendanceCollections";
import { db } from "../../firebase";
import { aggregateOtByEmployeeForMonth } from "../../helpers/payrollPunchAnalytics";
import { formatDurationHMM } from "../../helpers/staffDailyHours";
import { firstDefined, buildEmployeeCodeLookupForShop } from "../../helpers/payrollAttendanceCore";

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

export default function PayrollOtSummaryReport() {
  const storedUser = useMemo(() => getStoredUser(), []);
  const visibleBranches = useMemo(() => getVisibleAttendanceBranches(storedUser), [storedUser]);
  const branchPool = visibleBranches.length ? visibleBranches : ATTENDANCE_BRANCHES;

  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [shopLabel, setShopLabel] = useState(branchPool[0]?.label || ATTENDANCE_BRANCHES[0].label);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState("");
  const [detailRow, setDetailRow] = useState(null);

  const currentShop = useMemo(
    () => branchPool.find((b) => b.label === shopLabel) || branchPool[0],
    [branchPool, shopLabel]
  );

  const shiftMonth = (delta) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));

  const load = useCallback(async () => {
    if (!currentShop) return;
    setLoading(true);
    try {
      const punchCandidates = currentShop.punchCandidates || [];
      const punchName = (await resolveCollectionName(punchCandidates)) || punchCandidates[0];
      if (!punchName) throw new Error("Punch collection not found");

      const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
      const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 1);

      const empCol = await resolveCollectionName(currentShop.employeeCandidates || []);
      if (!empCol) throw new Error("Employees collection not found");
      const empSnap = await getDocs(collection(db, empCol));
      const nameById = {};
      empSnap.forEach((docSnap) => {
        const d = docSnap.data() || {};
        const id = String(
          firstDefined(d, ["employeeId", "empId", "uid", "user_id", "userId", "code", "employeeCode", "EmployeeId"]) ||
            docSnap.id
        );
        nameById[id] = String(
          firstDefined(d, ["name", "employeeName", "fullName", "empName", "Name", "EmployeeName"]) || id
        );
      });

      const employeeCodeLookup = buildEmployeeCodeLookupForShop(currentShop, empSnap.docs);
      const { byEmployee, mode: loadMode } = await aggregateOtByEmployeeForMonth(
        punchName,
        monthStart,
        monthEnd,
        employeeCodeLookup
      );
      setMode(loadMode);
      const list = Object.values(byEmployee)
        .map((x) => ({
          ...x,
          lackMs: Number(x.lackMs) || 0,
          byDay: Array.isArray(x.byDay) ? x.byDay : [],
          name: nameById[x.employeeId] || x.employeeId,
          otFormatted: formatDurationHMM(x.otMs),
          lackFormatted: formatDurationHMM(Number(x.lackMs) || 0),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setRows(list);
      setDetailRow(null);
      toast.success("OT computed (9h paid threshold, same as Daily hours)");
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed");
    } finally {
      setLoading(false);
    }
  }, [currentShop, month]);

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-4xl px-4 py-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">Payroll · Reports</p>
          <h1 className="text-2xl font-bold text-slate-900">OT summary (month)</h1>
          <p className="mt-1 text-sm text-slate-600">
            Overtime = paid time above <strong>9 hours</strong> in a day (after pairing punches), aligned with Daily hours.
            <span className="text-slate-500"> Below 9h</span> is attributed time still under that paid cap (no OT until paid
            time reaches 9h). Punch load mode: <span className="font-mono text-slate-800">{mode || "—"}</span>
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 py-1 shadow-sm">
            <button type="button" onClick={() => shiftMonth(-1)} className="rounded-lg p-1 hover:bg-slate-100">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="min-w-[10rem] text-center text-sm font-semibold">
              {month.toLocaleString("en-IN", { month: "long", year: "numeric" })}
            </span>
            <button type="button" onClick={() => shiftMonth(1)} className="rounded-lg p-1 hover:bg-slate-100">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <label className="text-xs font-semibold text-slate-700">
            Branch
            <select
              value={shopLabel}
              onChange={(e) => setShopLabel(e.target.value)}
              className="mt-1 block min-w-[10rem] rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
            >
              {branchPool.map((b) => (
                <option key={b.label} value={b.label}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Compute"}
          </button>
        </div>

        <p className="mt-3 text-xs text-slate-500">
          Click an employee row for each day: paid (paired) time, OT, and time under the 9h paid cap.
        </p>

        <div className="mt-2 overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-left text-xs">
            <thead className="bg-slate-800 text-[10px] font-bold uppercase tracking-wide text-slate-100">
              <tr>
                <th className="px-2 py-2">Employee</th>
                <th className="px-2 py-2 text-right">OT (total)</th>
                <th
                  className="px-2 py-2 text-right max-w-[9rem] leading-tight"
                  title="Sum of daily under-9h paid gaps on punch days this month"
                >
                  Below 9h (month)
                </th>
                <th className="px-2 py-2 text-right">Days with OT</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                    Click Compute.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.employeeId}
                    role="button"
                    tabIndex={0}
                    onClick={() => setDetailRow(r)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setDetailRow(r);
                      }
                    }}
                    className="border-t border-slate-100 cursor-pointer hover:bg-indigo-50/80 transition-colors"
                  >
                    <td className="px-2 py-1.5">
                      <div className="font-medium">{r.name}</div>
                      <div className="font-mono text-[10px] text-slate-500">{r.employeeId}</div>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.otFormatted}</td>
                    <td
                      className={`px-2 py-1.5 text-right font-mono tabular-nums ${
                        r.lackMs > 0 ? "text-amber-800 font-semibold" : "text-slate-400"
                      }`}
                    >
                      {r.lackFormatted}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.daysWithOt}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {detailRow && (
          <div
            className="fixed inset-0 z-[1200] flex items-end justify-center bg-slate-900/50 p-2 backdrop-blur-[1px] md:items-center"
            onClick={() => setDetailRow(null)}
            role="presentation"
          >
            <div
              className="max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-2xl md:rounded-2xl"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="ot-detail-title"
            >
              <div className="flex items-start justify-between gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
                <div className="min-w-0">
                  <h2 id="ot-detail-title" className="text-sm font-bold text-slate-900">
                    OT by day — {detailRow.name}
                  </h2>
                  <p className="mt-0.5 font-mono text-[10px] text-slate-500">{detailRow.employeeId}</p>
                  <p className="mt-1 text-[10px] text-slate-500">
                    {month.toLocaleString("en-IN", { month: "long", year: "numeric" })} · Paid = paired punch time in the
                    day; OT = paid time over 9h; Below 9h = paid time still under the 9h cap (not absence).
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setDetailRow(null)}
                  className="shrink-0 rounded-lg p-2 text-slate-500 hover:bg-slate-200/80"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="max-h-[min(60vh,420px)] overflow-auto p-3">
                {detailRow.byDay.length === 0 ? (
                  <p className="px-2 py-6 text-center text-sm text-slate-500">No punch days in range.</p>
                ) : (
                  <table className="min-w-full text-left text-xs">
                    <thead className="sticky top-0 bg-white text-[10px] font-bold uppercase tracking-wide text-slate-500 border-b border-slate-200">
                      <tr>
                        <th className="py-2 pr-2">Date</th>
                        <th className="py-2 pr-1 text-right">Paid</th>
                        <th className="py-2 pr-1 text-right">OT</th>
                        <th className="py-2 text-right">Below 9h</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailRow.byDay.map((d) => (
                        <tr key={d.dayKey} className="border-b border-slate-50">
                          <td className="py-1.5 pr-2 font-medium text-slate-800">
                            {(() => {
                              const [y, mo, da] = d.dayKey.split("-").map(Number);
                              if (!y || !mo || !da) return d.dayKey;
                              return new Date(y, mo - 1, da).toLocaleDateString("en-IN", {
                                weekday: "short",
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              });
                            })()}
                          </td>
                          <td className="py-1.5 pr-1 text-right font-mono tabular-nums text-slate-700">
                            {formatDurationHMM(Number(d.attributedMs) || 0)}
                          </td>
                          <td
                            className={`py-1.5 pr-1 text-right font-mono tabular-nums ${
                              d.otMs > 0 ? "text-indigo-700 font-semibold" : "text-slate-400"
                            }`}
                          >
                            {formatDurationHMM(Number(d.otMs) || 0)}
                          </td>
                          <td
                            className={`py-1.5 text-right font-mono tabular-nums ${
                              Number(d.lackMs) > 0 ? "text-amber-800 font-semibold" : "text-slate-400"
                            }`}
                          >
                            {formatDurationHMM(Number(d.lackMs) || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
